// Site/pipeline constants extracted from monolith

// Colors — Lightning Partners HQ dark palette
export const N = '#1C1C1E';
export const NL = '#2A2A2E';
export const R = '#4ADE80';
export const RL = '#2A2A2E';
export const COLORS = ['#1C1C1E', '#0A0A0A', '#0d9488', '#ca8a04', '#6366f1', '#4ADE80', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4'];

// Priority colors — simplified green/grey palette
export const pC = { Critical: '#EF4444', High: '#F59E0B', Medium: 'rgba(255,255,255,0.55)', Low: '#4ADE80', Conditional: 'rgba(255,255,255,0.40)' };

// Status icons
export const sI = { 'Not Started': 'circle', 'In Progress': 'circle-dot', Blocked: 'x-circle', 'Awaiting Review': 'clock', Complete: 'check-circle' };

// Status colors — green accent dominant
export const sC = { 'Not Started': 'rgba(255,255,255,0.30)', 'In Progress': '#4ADE80', Blocked: '#EF4444', 'Awaiting Review': '#F59E0B', Complete: '#4ADE80' };

// Status list
export const STS = ['Not Started', 'In Progress', 'Blocked', 'Awaiting Review', 'Complete'];

// Status cycle (same as STS)
export const STATUS_CYCLE = STS;
export const STATUS_COLORS = sC;

// Pipeline stages
export const STAGES = [
  { key: 'identified', label: 'Identified', color: 'rgba(255,255,255,0.50)' },
  { key: 'fit_analysis', label: 'Fit Analysis', color: '#A78BFA' },
  { key: 'loi', label: 'LOI', color: '#F59E0B' },
  { key: 'under_contract', label: 'Under Contract', color: '#4ADE80' },
  { key: 'active_site', label: 'Active Site', color: '#2DD4BF' },
  { key: 'sold', label: 'Sold to Off Taker', color: '#4ADE80' },
  { key: 'archived', label: 'Archived', color: 'rgba(255,255,255,0.30)' },
];

export const STAGE_MAP = {};
STAGES.forEach(s => { STAGE_MAP[s.key] = s; });

// Phase icon map
export const PHASE_EMOJI = {
  'Due Diligence': 'search',
  'Entitlement': 'clipboard-list',
  'Entitlements': 'clipboard-list',
  'Utility': 'zap',
  'Construction': 'hard-hat',
  'Design': 'ruler',
  'Permitting': 'scroll',
  'Pre-Construction': 'home',
  'Pre-Development': 'package',
  'Disposition': 'dollar-sign',
  'Closing': 'handshake',
};

// Helpers
import { renderIcon } from './icons';

export function getPhaseEmoji(phase, size) {
  let iconName = PHASE_EMOJI[phase];
  if (!iconName) {
    for (const key of Object.keys(PHASE_EMOJI)) {
      if (phase.toLowerCase().includes(key.toLowerCase())) { iconName = PHASE_EMOJI[key]; break; }
    }
  }
  return renderIcon(iconName || 'folder', size || 18, 'currentColor');
}

export function statusDotColor(status) {
  if (status === 'complete') return '#4ADE80';
  if (status === 'in-progress') return '#F59E0B';
  if (status === 'blocked') return '#EF4444';
  return 'rgba(255,255,255,0.30)';
}

export function fitScoreColor(score) {
  if (score == null || score === '') return 'rgba(255,255,255,0.30)';
  const s = Number(score);
  if (s <= 30) return '#EF4444';
  if (s <= 60) return '#F59E0B';
  if (s <= 80) return '#4ADE80';
  return '#4ADE80';
}

export function fitScoreBg(score) { return fitScoreColor(score) + '20'; }

export function formatDateP(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateShort(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Workflow template
export const SITE_WORKFLOW_TEMPLATE = [
  { name: 'Site Screening', phase: 'Find It', summary: 'Screen the opportunity — can we do anything with this property?', sort_order: 0, tasks: [
    { doc: 'Property identification & market analysis', priority: 'High' },
    { doc: 'Preliminary financial feasibility study', priority: 'High' },
    { doc: 'Municipal contact list', priority: 'Medium' },
    { doc: 'Zoning classification letter', priority: 'High' },
    { doc: 'Comprehensive plan / land use review', priority: 'Medium' },
  ]},
  { name: 'Environmental & Risk', phase: 'Vet It', summary: 'Environmental, flood, species, fire, and seismic risk assessment', sort_order: 1, tasks: [
    { doc: 'Phase I Environmental Site Assessment', priority: 'Critical' },
    { doc: 'FEMA Flood Zone Determination', priority: 'High' },
    { doc: 'Wetlands Delineation Report', priority: 'High' },
    { doc: 'Threatened & Endangered Species review', priority: 'Medium' },
    { doc: 'Fire Mitigation Plan / Hazard Review', priority: 'Medium' },
    { doc: 'Centralia Mine Fire Analysis', priority: 'Medium' },
    { doc: 'Seismic Hazard Review', priority: 'Low' },
    { doc: 'Adjacent Land Use / Proximity', priority: 'Medium' },
  ]},
  { name: 'Geotechnical & Subsurface', phase: 'Vet It', summary: 'Subsurface investigation, voids, soil gas, and thermal profiling', sort_order: 2, tasks: [
    { doc: 'Phase I Geotechnical Investigation', priority: 'Critical' },
    { doc: 'Subsurface Void / Mine Mapping', priority: 'High' },
    { doc: 'Soil Gas Migration Study', priority: 'High' },
    { doc: 'Subsurface Thermal Profiling', priority: 'Medium' },
  ]},
  { name: 'Land & Title', phase: 'Vet It', summary: 'Survey, title, deeds, easements, and boundary verification', sort_order: 3, tasks: [
    { doc: 'ALTA/NSPS Land Title Survey', priority: 'Critical' },
    { doc: 'Parcel Maps (all parcels)', priority: 'High' },
    { doc: 'Deed(s) & Title Reports', priority: 'Critical' },
    { doc: 'Title search & title insurance', priority: 'Critical' },
    { doc: 'Boundary Survey / Plat Map', priority: 'High' },
    { doc: 'Existing Easement Documentation', priority: 'Medium' },
    { doc: 'Topographic Survey', priority: 'High' },
  ]},
  { name: 'Negotiation & Contract', phase: 'Buy It', summary: 'LOI, purchase agreements, earnest money, and appraisal', sort_order: 4, tasks: [
    { doc: 'Letter of Intent (LOI) preparation', priority: 'High' },
    { doc: 'Option / Purchase Agreements', priority: 'High' },
    { doc: 'Earnest money deposit', priority: 'High' },
    { doc: 'Appraisal', priority: 'High' },
    { doc: 'Purchase & Sale Agreement (PSA) execution', priority: 'Critical' },
  ]},
  { name: 'Close Acquisition', phase: 'Buy It', summary: 'Closing coordination, title, funding, and recorded deed', sort_order: 5, tasks: [
    { doc: 'Closing coordination', priority: 'High' },
    { doc: 'Title commitment & insurance', priority: 'Critical' },
    { doc: 'Wire transfer / funding confirmation', priority: 'Critical' },
    { doc: 'Recorded deed & closing docs', priority: 'Critical' },
  ]},
  { name: 'Entitlements & Zoning', phase: 'Entitle It', summary: 'Zoning verification, municipal approvals, and land use entitlements', sort_order: 6, tasks: [
    { doc: 'Zoning verification & compliance review', priority: 'High' },
    { doc: 'Zoning change/variance application (if needed)', priority: 'High' },
    { doc: 'Site plan preparation', priority: 'High' },
    { doc: 'Planning commission submission', priority: 'High' },
    { doc: 'Public hearing attendance', priority: 'Critical' },
    { doc: 'Conditional use permit (if required)', priority: 'Medium' },
    { doc: 'Community outreach / neighbor notification', priority: 'Medium' },
    { doc: 'Entitlement approval received', priority: 'Critical' },
  ]},
  { name: 'Utility Coordination', phase: 'Entitle It', summary: 'Power, water, sewer, fiber availability and routing', sort_order: 7, tasks: [
    { doc: 'Utility Provider ID & Territory Map', priority: 'High' },
    { doc: 'Power & Electrical (PJM Queue, Rate Tariff, 230kV Routing)', priority: 'Critical' },
    { doc: 'Water Availability Letter', priority: 'High' },
    { doc: 'Sewer Availability Letter', priority: 'High' },
    { doc: 'Domestic Water Source Assessment', priority: 'Medium' },
    { doc: 'Water Source & Quality Report', priority: 'Medium' },
    { doc: 'Stormwater Management Plan', priority: 'High' },
    { doc: 'Fiber Availability Assessment & Lit/Dark Maps', priority: 'High' },
    { doc: 'Diverse Route Feasibility', priority: 'Medium' },
  ]},
  { name: 'Deep Diligence', phase: 'Entitle It', summary: 'Phase II studies triggered by Phase I findings', sort_order: 8, tasks: [
    { doc: 'Phase II ESA', priority: 'High' },
    { doc: 'Phase II Geotechnical', priority: 'High' },
    { doc: 'AMD Baseline Assessment', priority: 'Medium' },
    { doc: 'Flood zone determination / FEMA review', priority: 'Medium' },
    { doc: 'Utility availability verification', priority: 'Medium' },
  ]},
  { name: 'Pre-Construction Readiness', phase: 'Entitle It', summary: 'Road access, traffic, aerial photography, and engineering studies', sort_order: 9, tasks: [
    { doc: 'Road Access Documentation', priority: 'High' },
    { doc: 'Traffic Impact Study', priority: 'Medium' },
    { doc: 'Aerial / Drone Photography', priority: 'Medium' },
    { doc: 'Prior Engineering Studies', priority: 'Medium' },
    { doc: '69kV Line Rerouting Feasibility', priority: 'High' },
  ]},
  { name: 'Deliverable Package — Documents', phase: 'Package & Sell', summary: 'All completed reports, surveys, and assessments for 5C', sort_order: 10, tasks: [
    { doc: 'Executive site summary / investment memo', priority: 'Critical' },
    { doc: 'All Phase I & Phase II reports (Environmental, Geotech)', priority: 'Critical' },
    { doc: 'ALTA/NSPS survey & boundary/plat maps', priority: 'Critical' },
    { doc: 'Parcel maps (all parcels)', priority: 'High' },
    { doc: 'Deed(s) & title reports', priority: 'Critical' },
    { doc: 'Title insurance policy / commitment', priority: 'Critical' },
    { doc: 'Topographic survey', priority: 'High' },
    { doc: 'Wetlands delineation report', priority: 'High' },
    { doc: 'FEMA Flood Zone determination letter', priority: 'High' },
    { doc: 'Threatened & Endangered Species report', priority: 'Medium' },
    { doc: 'Fire / hazard mitigation plans', priority: 'Medium' },
    { doc: 'Subsurface void / mine mapping report', priority: 'High' },
    { doc: 'Soil gas migration study', priority: 'High' },
    { doc: 'Subsurface thermal profiling results', priority: 'Medium' },
    { doc: 'Aerial / drone photography', priority: 'Medium' },
    { doc: 'Historical aerial photography', priority: 'Low' },
  ]},
  { name: 'Deliverable Package — Entitlements & Approvals', phase: 'Package & Sell', summary: 'All zoning, entitlement, and approval documentation for 5C', sort_order: 11, tasks: [
    { doc: 'Zoning classification letter & applicable ordinances', priority: 'Critical' },
    { doc: 'Zoning approval / variance documentation', priority: 'Critical' },
    { doc: 'Conditional use permit (if applicable)', priority: 'High' },
    { doc: 'Entitlement approval letter', priority: 'Critical' },
    { doc: 'Approved site plan', priority: 'Critical' },
    { doc: 'Planning commission approval documentation', priority: 'High' },
    { doc: 'All hearing records / minutes', priority: 'Medium' },
    { doc: 'Conditions of approval (with compliance status)', priority: 'High' },
  ]},
  { name: 'Deliverable Package — Utility & Infrastructure', phase: 'Package & Sell', summary: 'All utility availability, routing, and infrastructure documentation for 5C', sort_order: 12, tasks: [
    { doc: 'Utility provider territory maps', priority: 'High' },
    { doc: 'All utility availability letters (power, water, sewer, fiber)', priority: 'Critical' },
    { doc: 'PJM Interconnection Queue position documentation', priority: 'Critical' },
    { doc: 'Rate tariff schedules', priority: 'High' },
    { doc: '230kV line routing & ROW documentation', priority: 'High' },
    { doc: 'On-Site generation asset documentation', priority: 'Medium' },
    { doc: 'CSA terms', priority: 'High' },
    { doc: 'Water source & quality report', priority: 'High' },
    { doc: 'Stormwater management plan', priority: 'High' },
    { doc: 'Fiber route feasibility / lit-dark maps', priority: 'High' },
    { doc: 'Road access documentation', priority: 'High' },
    { doc: 'Traffic impact study', priority: 'Medium' },
  ]},
  { name: 'Deliverable Package — Financial & Legal', phase: 'Package & Sell', summary: 'Financial summary, legal documents, and community records for 5C', sort_order: 13, tasks: [
    { doc: 'Purchase & Sale Agreement (executed copy)', priority: 'Critical' },
    { doc: 'Option agreements (if applicable)', priority: 'Medium' },
    { doc: 'Appraisal', priority: 'High' },
    { doc: 'Financial summary (acquisition, DD, entitlement, sale, margin)', priority: 'Critical' },
    { doc: 'Easement documentation', priority: 'High' },
    { doc: 'Municipal contact list', priority: 'Medium' },
    { doc: 'Community outreach records', priority: 'Medium' },
  ]},
  { name: 'Closing', phase: 'Package & Sell', summary: 'Final closing process with 5C Group', sort_order: 14, tasks: [
    { doc: 'Closing checklist review', priority: 'Critical' },
    { doc: 'Purchase agreement execution', priority: 'Critical' },
    { doc: 'Closing statement preparation', priority: 'High' },
    { doc: 'Title commitment & insurance', priority: 'Critical' },
    { doc: 'Executed purchase agreement delivery', priority: 'Critical' },
    { doc: 'Wire transfer / funding confirmation', priority: 'Critical' },
    { doc: 'Recorded deed & closing docs', priority: 'Critical' },
    { doc: 'Title transfer & recording', priority: 'Critical' },
    { doc: 'Final accounting & profit distribution', priority: 'High' },
  ]},
];

export const TEMPLATE_TOTAL_TASKS = SITE_WORKFLOW_TEMPLATE.reduce((s, w) => s + w.tasks.length, 0);

// D2: Conditional item evaluation
export function evaluateCondition(rule, siteProperties) {
  if (!rule || !siteProperties) return true;
  try {
    const { property, operator, value } = typeof rule === 'string' ? JSON.parse(rule) : rule;
    if (!property) return true;
    const actual = siteProperties[property];
    if (actual === undefined || actual === null) return true;
    switch (operator) {
      case 'equals': return String(actual).toLowerCase() === String(value).toLowerCase();
      case 'not_equals': return String(actual).toLowerCase() !== String(value).toLowerCase();
      case 'contains': return String(actual).toLowerCase().includes(String(value).toLowerCase());
      case 'not_contains': return !String(actual).toLowerCase().includes(String(value).toLowerCase());
      case 'gt': return Number(actual) > Number(value);
      case 'lt': return Number(actual) < Number(value);
      case 'in': return (Array.isArray(value) ? value : String(value).split(',')).map(v => v.trim().toLowerCase()).includes(String(actual).toLowerCase());
      case 'truthy': return !!actual;
      case 'falsy': return !actual;
      default: return true;
    }
  } catch { return true; }
}

// Compute phase progress from workstreams list
export function computePhaseProgress(wsList) {
  const phaseOrder = ['Find It', 'Vet It', 'Buy It', 'Entitle It', 'Package & Sell'];
  const nameToPhase = {};
  SITE_WORKFLOW_TEMPLATE.forEach(t => { nameToPhase[t.name] = t.phase; });
  const phaseMap = {};
  phaseOrder.forEach(p => phaseMap[p] = { name: p, total: 0, complete: 0, inProgress: 0, blocked: 0, workstreams: [] });
  (wsList || []).forEach(ws => {
    let phase = ws.phase || nameToPhase[ws.name] || '';
    if (phase && !phaseMap[phase]) {
      const match = phaseOrder.find(p => p.toLowerCase().startsWith(phase.toLowerCase().substring(0, 8)));
      if (match) phase = match;
    }
    if (!phase || !phaseMap[phase]) phase = phaseOrder[0];
    phaseMap[phase].workstreams.push(ws.name);
    const items = ws.items || [];
    phaseMap[phase].total += items.length;
    phaseMap[phase].complete += items.filter(i => i.status === 'Complete').length;
    phaseMap[phase].inProgress += items.filter(i => i.status === 'In Progress').length;
    phaseMap[phase].blocked += items.filter(i => i.status === 'Blocked').length;
  });
  return phaseOrder.map(p => {
    const d = phaseMap[p];
    const status = d.total === 0 ? 'empty' : d.complete === d.total ? 'complete' : d.blocked > 0 ? 'blocked' : (d.inProgress > 0 || d.complete > 0) ? 'in-progress' : 'not-started';
    return { ...d, status };
  });
}

export function getCurrentPhaseLabel(wsList) {
  const phases = computePhaseProgress(wsList);
  for (const p of phases) {
    if (p.status !== 'complete' && p.status !== 'empty') return p.name;
  }
  const anyComplete = phases.some(p => p.status === 'complete');
  if (anyComplete) return 'Complete';
  return null;
}
